import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Package, MapPin, Star, ChevronRight, ChevronDown, Loader, X } from 'lucide-react';
import { XpDisplay } from './XpDisplay';
import { useUser } from '../context/UserContext';
import { ThemeToggle } from '../arrangement/ThemeToggle';
import API_URL from '../config/ApiUrl';
import { DispensaryDetailModal } from '../dispensary/DispensaryPage';
import { UnifiedSearchDropdown } from './UnifiedSearchDropdown';
import { useTheme } from '../arrangement/ThemeContext';
import { NotificationBell } from './NotificationBell';

const users = [
  { id: 1, tier: "Connoisseur", xp: 1500, nextLevelXp: 2000, avatar: "/placeholder.svg" }
];

// Category Icons Component
const CategoryIcons = {
  flower: () => <img src="/branding/menu/flower.svg" alt="Flower" style={{ width: '2rem', height: '2rem', filter: 'brightness(0) invert(1)' }} />,
  cart: () => <img src="/branding/menu/cart.svg" alt="Cartridge/Vape" style={{ width: '2rem', height: '2rem', filter: 'brightness(0) invert(1)' }} />,
  edible: () => <img src="/branding/menu/edibble.svg" alt="Edible" style={{ width: '2rem', height: '2rem', filter: 'brightness(0) invert(1)' }} />,
  'pre-roll': () => <img src="/branding/menu/prerol.svg" alt="Pre-roll" style={{ width: '2rem', height: '2rem', filter: 'brightness(0) invert(1)' }} />,
  concentrate: () => <img src="/branding/menu/_con.svg" alt="Concentrate" style={{ width: '2rem', height: '2rem', filter: 'brightness(0) invert(1)' }} />,
  other: () => <img src="/branding/menu/Frame.svg" alt="Other" style={{ width: '2rem', height: '2rem', filter: 'brightness(0) invert(1)' }} />
};

export function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
    
  const { colors } = useTheme();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDispensaryDetailOpen, setIsDispensaryDetailOpen] = useState(false);
  const [selectedDispensaryId, setSelectedDispensaryId] = useState(null);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  
  const [theme, setTheme] = useState('dark');

  const dropdownTimeoutRef = useRef(null);

  const handleDropdownMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setIsProductsDropdownOpen(true);
  };

  const handleDropdownMouseLeave = () => {
       dropdownTimeoutRef.current = setTimeout(() => {
         setIsProductsDropdownOpen(false);
       }, 200);
  };

  const { 
    user, 
    isAuthenticated, 
    logout, 
    loading, 
    userStats, 
    statsLoading 
  } = useUser();

  const dropdownRef = useRef(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProductsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatSmokerLevel = (level) => {
    if (!level) return 'Member';
    return level.charAt(0).toUpperCase() + level.slice(1);
  };
 
  const currentUser = users[0];

  // Navigation items with their corresponding routes
  const navigationItems = [
    { name: 'Discover', path: '/discover' },
    { name: 'Community', path: '/community' },
    { name: 'Threads', path: '/threads' },
    { name: 'Sesh Rooms', path: '/sesh-rooms' },
    { name: 'Research Hub', path: '/ResearchHub' },
    { name: 'Grow Room', path: '/grow-room' }
  ];

  // Function to check if a navigation item is active
  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const handleNavigation = (item) => {
    const routes = {
      'Products': '/products',
      'Discover': '/discover',
      'Community': '/community',
      'Threads': '/threads',
      'Sesh Rooms': '/sesh-rooms',
      'Research Hub': '/ResearchHub',
      'AI Assistant': '/ai-discover',
      'Grow Room': '/grow-room'
    };
    navigate(routes[item]);
    setIsMobileMenuOpen(false);
    setIsProductsDropdownOpen(false);
  };

  const handleAuthClick = () => {
    if (isAuthenticated) {
      logout();
    } else {
      navigate('/login');
    }
  };

  const handleUserClick = () => {
    if (isAuthenticated) {
      navigate('/profile');
    }
  };

  const handleDispensarySelect = (dispensary) => {
    setSelectedDispensaryId(dispensary.id);
    setIsDispensaryDetailOpen(true);
  };

  const getSmokerEmoji = (level) => {
    switch(level) {
      case 'beginner': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/cdb53b7a8c7e4624a810c0d654ca03c8_Smoker_beginer.webp';
      case 'casual': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/e72017f33d1c480db1928ca3d7295de4_Smoker_Level_cas.webp';
      case 'connoisseur': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/3e1e13711dd441fcb7c34c155416ee69_Smoker_Level_con.webp';
      case 'stoner': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/9084554e27d747188148530bc7899eee_Smoker_Level_Logo_Stoner.webp';
      default: return  'https://highmind-bucket.s3.us-east-1.amazonaws.com/6d4ba06007624c3691fd682dfbb31cdf_hmind.png'
    }
  }
  
  const handleCategoryClick = (category) => {
    console.log('Category clicked:', category);
    console.log('Navigating to:', `/products?category=${category}`);
    navigate(`/products?category=${category}`);
    setIsProductsDropdownOpen(false);
  };

  const handleStrainTypeClick = (strainType) => {
    console.log('STRAIN TYPE')
    navigate(`/products?strain_type=${strainType}`);
    setIsProductsDropdownOpen(false);
  };

  return (
    <>
      <header style={{
        backgroundColor: colors.bgSecondary || '#0a0a0a',
        borderBottom: `1px solid ${colors.border || '#333'}`,
        padding: '0.5rem 0',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        boxShadow: '0 0.125rem 0.5rem rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
          padding:  window.innerWidth <= 768 ? '0 0.5rem' : '0 1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '3rem'
        }}>
          {/* Left Section */}
          <div style={{ 
            display: 'flex', 
            marginLeft: window.innerWidth > 1200 ? '4.25rem' : '',
            alignItems: 'center', 
            gap: window.innerWidth > 1200 ? '1.5rem' : '1rem',
            flex: '1',
            minWidth: 0
          }}>
            <div 
              onClick={() => navigate('/')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontFamily: "'Manrope', sans-serif",
                fontSize: window.innerWidth > 768 ? '1.25rem' : '1.125rem',
                fontWeight: 'bold',
                color: colors.accentPrimary1,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                position: 'relative',
                flexShrink: 0
              }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/gREEN.svg`}
                alt="HighMind Logo"
                style={{
                  width: '1.5rem',
                  height: '1.5rem',
                }}
              />
              Oak
              
              {/* Beta Badge */}
              <span style={{
                position: 'absolute',
                top: '-0.6rem',
                right: '-1.3rem',
                backgroundColor: '#666',
                color: '#fff',
                fontSize: '0.5rem',
                fontWeight: '600',
                padding: '0.1rem 0.3rem',
                borderRadius: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.025rem',
                lineHeight: '1',
                border: '1px solid #888',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
              }}>
                BETA
              </span>
            </div>
            
            {/* Desktop Navigation */}
            <nav style={{ 
              display: window.innerWidth > 768 ? 'flex' : 'none',
              gap: window.innerWidth > 1200 ? '1.25rem' : '0.875rem',
              alignItems: 'center',
              overflow: 'visible'
            }}>
              {/* Products with Dropdown */}
              <div 
                ref={dropdownRef}
                onMouseEnter={handleDropdownMouseEnter}
                onMouseLeave={handleDropdownMouseLeave}
                style={{ 
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  overflow: 'visible'
                }}
              >
                <a 
                  onClick={() => handleNavigation('Products')}
                  style={{
                    color: isActiveRoute('/products') ? '#00d4aa' : '#ccc',
                    textDecoration: 'none',
                    fontSize: window.innerWidth > 1200 ? '0.875rem' : '0.8rem',
                    fontWeight: isActiveRoute('/products') ? '600' : '500',
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    whiteSpace: 'nowrap',
                    padding: '0.25rem 0',
                    borderBottom: `0.125rem solid ${isActiveRoute('/products') ? '#00d4aa' : 'transparent'}`
                  }}
                  onMouseEnter={(e) => {
                    if (!isActiveRoute('/products')) {
                      e.target.style.color = '#00d4aa';
                      e.target.style.borderBottomColor = '#00d4aa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActiveRoute('/products')) {
                      e.target.style.color = '#ccc';
                      e.target.style.borderBottomColor = 'transparent';
                    }
                  }}
                >
                  Products
                </a>
                
                {/* Dropdown Arrow Button */}
                <button
                  onClick={() => setIsProductsDropdownOpen(!isProductsDropdownOpen)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: isProductsDropdownOpen ? '#00d4aa' : '#ccc',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.2s ease',
                    transform: isProductsDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <ChevronDown size={14} />
                </button>

                {/* Dropdown Menu */}
                {isProductsDropdownOpen && (
                  <div
                    style={{
                      position: window.innerWidth <= 768 ? 'fixed' : 'absolute',
                      top: window.innerWidth <= 768 ? '3.5rem' : 'calc(100% + 0.5rem)',
                      left: window.innerWidth <= 768 ? '0' : '15rem',
                      right: window.innerWidth <= 768 ? '0' : 'auto',
                      transform: window.innerWidth <= 768 ? 'none' : 'translateX(-50%)',
                      // CRITICAL FIX: Use viewport-relative positioning
                      marginLeft: window.innerWidth > 768 ? '50%' : '0',
                      background: colors.bgTertiary || '#1a1a1a',
                      border: `1px solid ${colors.border || '#333'}`,
                      borderRadius: window.innerWidth <= 768 ? '0' : '16px',
                      padding: window.innerWidth <= 480 ? '1rem' : window.innerWidth <= 768 ? '1.25rem' : '1.5rem',
                      // CRITICAL FIX: Max width based on viewport, not fixed width
                      width: window.innerWidth <= 768 
                        ? '100%' 
                        : 'min(600px, calc(100vw - 2rem))',
                      maxWidth: window.innerWidth <= 768 ? 'none' : 'calc(100vw - 2rem)',
                      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
                      zIndex: 9999,
                      pointerEvents: 'auto',
                      maxHeight: window.innerWidth <= 768 ? 'calc(100vh - 4rem)' : '85vh',
                      overflowY: 'auto',
                      overflowX: 'hidden'
                    }}
                  >
                    {/* Close button for mobile */}
                    {window.innerWidth <= 768 && (
                      <button
                        onClick={() => setIsProductsDropdownOpen(false)}
                        style={{
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                          background: 'transparent',
                          border: 'none',
                          color: '#ccc',
                          cursor: 'pointer',
                          padding: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 10000
                        }}
                      >
                        <X size={20} />
                      </button>
                    )}

                    <h4 style={{
                      margin: '0 0 1rem 0',
                      fontSize: window.innerWidth <= 480 ? '0.875rem' : '1rem',
                      fontWeight: '600',
                      color: '#e5e5e5',
                      borderBottom: `1px solid ${colors.border || '#333'}`,
                      paddingBottom: '0.75rem',
                      paddingRight: window.innerWidth <= 768 ? '2rem' : '0'
                    }}>
                      Shop By Category
                    </h4>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: window.innerWidth <= 480 
                        ? 'repeat(2, 1fr)' 
                        : window.innerWidth <= 768 
                          ? 'repeat(3, 1fr)' 
                          : window.innerWidth <= 1100 // ADJUSTED for smaller viewports
                            ? 'repeat(3, 1fr)'
                            : 'repeat(4, 1fr)',
                      gap: window.innerWidth <= 480 ? '0.375rem' : '0.5rem'
                    }}>
                      {/* All Categories Option */}
                      <div
                        onClick={() => {
                          navigate('/products');
                          setIsProductsDropdownOpen(false);
                        }}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          padding: window.innerWidth <= 480 ? '0.75rem' : window.innerWidth <= 1100 ? '0.75rem' : '1rem',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          border: '2px solid transparent',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(0, 212, 170, 0.1)';
                          e.currentTarget.style.borderColor = '#00d4aa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                          e.currentTarget.style.borderColor = 'transparent';
                        }}
                      >
                        <Package size={window.innerWidth <= 480 ? 24 : window.innerWidth <= 1100 ? 28 : 32} style={{ 
                          color: '#00d4aa',
                          marginBottom: '0.5rem'
                        }} />
                        <span style={{
                          fontSize: window.innerWidth <= 480 ? '0.75rem' : window.innerWidth <= 1100 ? '0.8rem' : '0.875rem',
                          fontWeight: '600',
                          color: '#e5e5e5',
                          textAlign: 'center'
                        }}>
                          All Products
                        </span>
                      </div>

                      {/* Category Options */}
                      {Object.keys(CategoryIcons).map((category) => {
                        const IconComponent = CategoryIcons[category];
                        const displayName = category === 'cart' ? 'Vapes' : 
                                          category === 'pre-roll' ? 'Pre-rolls' : 
                                          category.charAt(0).toUpperCase() + category.slice(1);
                        
                        return (
                          <div
                            key={category}
                            onClick={() => handleCategoryClick(category)}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              padding: window.innerWidth <= 480 ? '0.75rem' : window.innerWidth <= 1100 ? '0.75rem' : '1rem',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              border: '2px solid transparent',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(0, 212, 170, 0.1)';
                              e.currentTarget.style.borderColor = '#00d4aa';
                              if (window.innerWidth > 768) {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                              e.currentTarget.style.borderColor = 'transparent';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <div style={{
                              marginBottom: '0.5rem',
                              color: '#888',
                              transform: window.innerWidth <= 480 ? 'scale(0.8)' : window.innerWidth <= 1100 ? 'scale(0.9)' : 'scale(1)'
                            }}>
                              <IconComponent />
                            </div>
                            <span style={{
                              fontSize: window.innerWidth <= 480 ? '0.7rem' : window.innerWidth <= 1100 ? '0.75rem' : '0.875rem',
                              fontWeight: '600',
                              color: '#e5e5e5',
                              textAlign: 'center'
                            }}>
                              {displayName}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div style={{
                      marginTop: '1rem',
                      paddingTop: '1rem',
                      borderTop: `1px solid ${colors.border || '#333'}`
                    }}>
                      <h4 style={{
                        margin: '0 0 1rem 0',
                        fontSize: window.innerWidth <= 480 ? '0.875rem' : '1rem',
                        fontWeight: '600',
                        color: '#e5e5e5',
                      }}>
                        Shop by Type
                      </h4>
                      
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: window.innerWidth <= 480 
                          ? 'repeat(1, 1fr)' 
                          : window.innerWidth <= 768 
                            ? 'repeat(2, 1fr)' 
                            : 'repeat(3, 1fr)',
                        gap: window.innerWidth <= 480 ? '0.375rem' : '0.5rem'
                      }}>
                        {/* Indica */}
                        <div
                          onClick={() => handleStrainTypeClick('Indica')}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            padding: window.innerWidth <= 480 ? '0.75rem' : window.innerWidth <= 1100 ? '0.75rem' : '1rem',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            backgroundColor: 'rgba(147, 51, 234, 0.1)',
                            border: '2px solid transparent',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(147, 51, 234, 0.2)';
                            e.currentTarget.style.borderColor = '#9333ea';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(147, 51, 234, 0.1)';
                            e.currentTarget.style.borderColor = 'transparent';
                          }}
                        >
                          <svg width={window.innerWidth <= 480 ? "48" : window.innerWidth <= 1100 ? "50" : "60"} height={window.innerWidth <= 480 ? "48" : window.innerWidth <= 1100 ? "50" : "60"} viewBox="0 0 60 60" >
                            <image 
                              href="https://highmind-bucket.s3.us-east-1.amazonaws.com/indica-removebg-preview.png" 
                              height={window.innerWidth <= 480 ? "48" : window.innerWidth <= 1100 ? "50" : "60"}
                              width={window.innerWidth <= 480 ? "48" : window.innerWidth <= 1100 ? "50" : "60"}
                              x="0" 
                              y="0" 
                            />
                          </svg>
                          <span style={{
                            fontSize: window.innerWidth <= 480 ? '0.75rem' : window.innerWidth <= 1100 ? '0.8rem' : '0.875rem',
                            fontWeight: '600',
                            color: '#9333ea',
                            textAlign: 'center'
                          }}>
                            Indica
                          </span>
                          <span style={{
                            fontSize: window.innerWidth <= 480 ? '0.6rem' : '0.65rem',
                            color: '#888',
                            textAlign: 'center',
                            marginTop: '4px'
                          }}>
                            Relaxing
                          </span>
                        </div>

                        {/* Hybrid */}
                        <div
                          onClick={() => handleStrainTypeClick('Hybrid')}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            padding: window.innerWidth <= 480 ? '0.75rem' : window.innerWidth <= 1100 ? '0.75rem' : '1rem',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                            border: '2px solid transparent',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.2)';
                            e.currentTarget.style.borderColor = '#10b981';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.1)';
                            e.currentTarget.style.borderColor = 'transparent';
                          }}
                        >
                          <svg width={window.innerWidth <= 480 ? "48" : window.innerWidth <= 1100 ? "50" : "60"} height={window.innerWidth <= 480 ? "48" : window.innerWidth <= 1100 ? "50" : "60"} viewBox="0 0 60 60">
                            <image 
                              href="https://highmind-bucket.s3.us-east-1.amazonaws.com/hybird (1).png" 
                              height={window.innerWidth <= 480 ? "48" : window.innerWidth <= 1100 ? "50" : "60"}
                              width={window.innerWidth <= 480 ? "48" : window.innerWidth <= 1100 ? "50" : "60"}
                              x="0" 
                              y="0" 
                            />
                          </svg>
                          <span style={{
                            fontSize: window.innerWidth <= 480 ? '0.75rem' : window.innerWidth <= 1100 ? '0.8rem' : '0.875rem',
                            fontWeight: '600',
                            color: '#10b981',
                            textAlign: 'center'
                          }}>
                            Hybrid
                          </span>
                          <span style={{
                            fontSize: window.innerWidth <= 480 ? '0.6rem' : '0.65rem',
                            color: '#888',
                            textAlign: 'center',
                            marginTop: '4px'
                          }}>
                            Balanced
                          </span>
                        </div>

                        {/* Sativa */}
                        <div
                          onClick={() => handleStrainTypeClick('Sativa')}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            padding: window.innerWidth <= 480 ? '0.75rem' : window.innerWidth <= 1100 ? '0.75rem' : '1rem',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            backgroundColor: 'rgba(245, 158, 11, 0.1)',
                            border: '2px solid transparent',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(245, 158, 11, 0.2)';
                            e.currentTarget.style.borderColor = '#f59e0b';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(245, 158, 11, 0.1)';
                            e.currentTarget.style.borderColor = 'transparent';
                          }}
                        >
                          <svg width={window.innerWidth <= 480 ? "48" : window.innerWidth <= 1100 ? "50" : "60"} height={window.innerWidth <= 480 ? "48" : window.innerWidth <= 1100 ? "50" : "60"} viewBox="0 0 60 60" >
                            <image 
                              href="https://highmind-bucket.s3.us-east-1.amazonaws.com/sativa (2).png" 
                              height={window.innerWidth <= 480 ? "48" : window.innerWidth <= 1100 ? "50" : "60"}
                              width={window.innerWidth <= 480 ? "48" : window.innerWidth <= 1100 ? "50" : "60"}
                              x="0" 
                              y="0" 
                            />
                          </svg>
                          <span style={{
                            fontSize: window.innerWidth <= 480 ? '0.75rem' : window.innerWidth <= 1100 ? '0.8rem' : '0.875rem',
                            fontWeight: '600',
                            color: '#f59e0b',
                            textAlign: 'center'
                          }}>
                            Sativa
                          </span>
                          <span style={{
                            fontSize: window.innerWidth <= 480 ? '0.6rem' : '0.65rem',
                            color: '#888',
                            textAlign: 'center',
                            marginTop: '4px'
                          }}>
                            Energizing
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div style={{
                      marginTop: '1rem',
                      paddingTop: '1rem',
                      borderTop: `1px solid ${colors.border || '#333'}`,
                      display: 'flex',
                      flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
                      gap: '0.5rem',
                      justifyContent: 'center'
                    }}>
                      <button
                        onClick={() => {
                          navigate('/products?sort=rating');
                          setIsProductsDropdownOpen(false);
                        }}
                        style={{
                          padding: window.innerWidth <= 480 ? '0.625rem 1rem' : '0.5rem 1rem',
                          background: 'rgba(245, 158, 11, 0.2)',
                          color: '#f59e0b',
                          border: '1px solid #f59e0b',
                          borderRadius: '8px',
                          fontSize: window.innerWidth <= 480 ? '0.8rem' : '0.75rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          width: window.innerWidth <= 480 ? '100%' : 'auto'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f59e0b';
                          e.currentTarget.style.color = '#0a0a0a';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(245, 158, 11, 0.2)';
                          e.currentTarget.style.color = '#f59e0b';
                        }}
                      >
                        ⭐ Top Rated
                      </button>
                      
                      <button
                        onClick={() => {
                          navigate('/products?filter=new');
                          setIsProductsDropdownOpen(false);
                        }}
                        style={{
                          padding: window.innerWidth <= 480 ? '0.625rem 1rem' : '0.5rem 1rem',
                          background: 'rgba(0, 212, 170, 0.2)',
                          color: '#00d4aa',
                          border: '1px solid #00d4aa',
                          borderRadius: '8px',
                          fontSize: window.innerWidth <= 480 ? '0.8rem' : '0.75rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          width: window.innerWidth <= 480 ? '100%' : 'auto'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#00d4aa';
                          e.currentTarget.style.color = '#0a0a0a';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(0, 212, 170, 0.2)';
                          e.currentTarget.style.color = '#00d4aa';
                        }}
                      >
                        🆕 New Arrivals
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Other Navigation Items */}
              {navigationItems.map(item => {
                const isActive = isActiveRoute(item.path);
                return (
                  <a 
                    key={item.name} 
                    onClick={() => handleNavigation(item.name)}
                    style={{
                      color: isActive ? '#00d4aa' : '#ccc',
                      textDecoration: 'none',
                      fontSize: window.innerWidth > 1200 ? '0.875rem' : '0.8rem',
                      fontWeight: isActive ? '600' : '500',
                      cursor: 'pointer',
                      transition: 'color 0.2s ease',
                      whiteSpace: 'nowrap',
                      padding: '0.25rem 0',
                      borderBottom: `0.125rem solid ${isActive ? '#00d4aa' : 'transparent'}`
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.target.style.color = '#00d4aa';
                        e.target.style.borderBottomColor = '#00d4aa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.target.style.color = '#ccc';
                        e.target.style.borderBottomColor = 'transparent';
                      }
                    }}
                  >
                    {item.name}
                  </a>
                );
              })}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{
                display: window.innerWidth <= 768 ? 'flex' : 'none',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                border: '1px solid #333',
                borderRadius: '0.5rem',
                padding: '0.5rem',
                color: '#ccc',
                cursor: 'pointer',
                fontSize: '1.25rem',
                flexShrink: 0
              }}
            >
              {isMobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>
          
          {/* Right Section */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: window.innerWidth > 1024 ? '0.75rem' : '0.5rem',
            flex: 'none'
          }}>
            {/* Theme Toggle - Desktop only */}
            {window.innerWidth > 768 && (
              <div style={{ flexShrink: 0 }}>
                <ThemeToggle />
              </div>
            )}

            {/* Unified Search - Hide on very small screens */}
            {window.innerWidth > 640 && (
              <div style={{ flexShrink: 0 }}>
                <UnifiedSearchDropdown onDispensarySelect={handleDispensarySelect} />
              </div>
            )}

            {isAuthenticated && user && (
              <div style={{ flexShrink: 0 }}>
                <NotificationBell userId={user.id} />
              </div>
            )}
            
            {/* User Stats */}
            {isAuthenticated && userStats && !statsLoading && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.375rem 0.625rem',
                borderRadius: '0.5rem',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                cursor: 'pointer',
                flexShrink: 0
              }}
              onClick={handleUserClick}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <div style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0
                  }}>
                    <div style={{
                      backgroundColor: userStats.profile_image_url ? 'transparent' : '#333',
                      borderRadius: '50%',
                      width: '2rem',
                      height: '2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center', 
                      fontSize: '1rem',
                      transition: 'background-color 0.2s ease',
                      backgroundImage: userStats.profile_image_url 
                        ? `url(${userStats.profile_image_url})` 
                        : `url(${userStats.avatar})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      border: '0.125rem solid rgba(0, 212, 170, 0.3)'
                    }}
                    >
                      {!userStats.profile_image_url}
                    </div>
                    
                    <img 
                    src={getSmokerEmoji(userStats.smoker_level)}
                    style={{
                      position: 'absolute',
                      bottom: '-0.125rem',
                      right: '-0.125rem',
                      backgroundColor: '#111',
                      borderRadius: '50%',
                      width: '1rem',
                      height: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '0.0625rem solid #00d4aa'
                    }}>
                    </img>
                  </div>
                  
                  {window.innerWidth > 900 && (
                    <div style={{ 
                      fontSize: '0.7rem',
                      color: '#ccc',
                      textAlign: 'left',
                      lineHeight: '1.3',
                      minWidth: 0
                    }}>
                      <div style={{ 
                        fontWeight: '600', 
                        color: '#00d4aa',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {formatSmokerLevel(userStats.smoker_level)}
                      </div>
                      <div style={{ 
                        color: '#888',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {userStats.username}
                      </div>
                    </div>
                  )}
                </div>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  alignItems: 'flex-start',
                  flexShrink: 0
                }}>
                  <div style={{
                    fontSize: '0.625rem',
                    color: '#00d4aa',
                    fontWeight: '600',
                    letterSpacing: '0.03125rem'
                  }}>
                    LV {userStats.level || Math.floor(userStats.xp / 500) + 1}
                  </div>
                  
                  <div style={{
                    width: '3.5rem',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '9999px',
                    height: '0.375rem'
                  }}>
                    <div 
                      style={{
                        backgroundColor: '#2563eb',
                        height: '0.375rem',
                        borderRadius: '9999px',
                        transition: 'width 0.3s ease',
                        width: `${userStats.progress_percentage}%`
                      }}
                    />
                  </div>
                </div>
                
                {window.innerWidth > 1024 && (
                  <span style={{
                    fontSize: '0.7rem',
                    color: '#6b7280',
                    whiteSpace: 'nowrap'
                  }}>
                    {userStats.xp} XP
                  </span>
                )}
              </div>
            )}

            {/* Stats Loading */}
            {isAuthenticated && statsLoading && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.375rem 0.625rem',
                borderRadius: '0.5rem',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                flexShrink: 0
              }}>
                <div style={{
                  backgroundColor: '#333',
                  borderRadius: '50%',
                  width: '2rem',
                  height: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem'
                }}>
                  ⏳
                </div>
                <div style={{ fontSize: '0.75rem', color: '#888' }}>
                  Loading...
                </div>
              </div>
            )}
            
            {/* AUTH BUTTON */}
            <button
              onClick={handleAuthClick}  
              style={{
                backgroundColor: isAuthenticated ? '#dc2626' : '#00d4aa',
                color: isAuthenticated ? '#fff' : '#000',
                border: 'none',
                borderRadius: '0.375rem',
                padding: window.innerWidth > 768 
                  ? (isAuthenticated ? '0.5rem 0.625rem' : '0.5rem 0.875rem')
                  : '0.5rem 0.625rem',
                fontSize: window.innerWidth > 768 ? '0.75rem' : '0.7rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
                fontFamily: "'Inter', sans-serif",
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                letterSpacing: '0.025rem',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                minWidth: 'fit-content'
              }}
              onMouseEnter={(e) => {
                if (isAuthenticated) {
                  e.target.style.backgroundColor = '#b91c1c';
                } else {
                  e.target.style.backgroundColor = '#00c299';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(0, 212, 170, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (isAuthenticated) {
                  e.target.style.backgroundColor = '#dc2626';
                } else {
                  e.target.style.backgroundColor = '#00d4aa';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                }
              }}
            >
              {window.innerWidth > 768 
                ? (isAuthenticated ? 'Out' : 'Sign Up | Log In')
                : (isAuthenticated ? 'Out' : 'Login')
              }
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && window.innerWidth <= 768 && (
          <div style={{
            backgroundColor: colors.bgTertiary,
            borderTop: `1px solid ${colors.border}`,
            padding: '1rem',
            maxHeight: 'calc(100vh - 3.5rem)',
            overflowY: 'auto'
          }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <ThemeToggle compact />
            </div>
            
            <nav style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}>
              {/* Products with Dropdown Toggle */}
              <div>
                <div
                  onClick={() => setIsProductsDropdownOpen(!isProductsDropdownOpen)}
                  style={{
                    color: isActiveRoute('/products') ? '#00d4aa' : '#ccc',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: isActiveRoute('/products') ? '600' : '500',
                    cursor: 'pointer',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    transition: 'background-color 0.2s ease',
                    backgroundColor: isActiveRoute('/products') ? 'rgba(0, 212, 170, 0.1)' : 'transparent',
                    borderLeft: isActiveRoute('/products') ? '3px solid #00d4aa' : '3px solid transparent',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>Products</span>
                  <ChevronDown 
                    size={16} 
                    style={{ 
                      transform: isProductsDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }} 
                  />
                </div>

                {/* Mobile Products Dropdown Content */}
                {isProductsDropdownOpen && (
                  <div style={{
                    marginTop: '0.5rem',
                    padding: '1rem',
                    backgroundColor: colors.bgSecondary,
                    borderRadius: '0.5rem',
                    border: `1px solid ${colors.border}`
                  }}>
                    <h4 style={{
                      margin: '0 0 0.75rem 0',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#e5e5e5',
                      borderBottom: `1px solid ${colors.border}`,
                      paddingBottom: '0.5rem'
                    }}>
                      Shop By Category
                    </h4>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '0.5rem',
                      marginBottom: '1rem'
                    }}>
                      {/* All Products */}
                      <div
                        onClick={() => {
                          navigate('/products');
                          setIsProductsDropdownOpen(false);
                          setIsMobileMenuOpen(false);
                        }}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid transparent'
                        }}
                      >
                        <Package size={28} style={{ color: '#00d4aa', marginBottom: '0.25rem' }} />
                        <span style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#e5e5e5',
                          textAlign: 'center'
                        }}>
                          All Products
                        </span>
                      </div>

                      {/* Categories */}
                      {Object.keys(CategoryIcons).map((category) => {
                        const IconComponent = CategoryIcons[category];
                        const displayName = category === 'cart' ? 'Vapes' : 
                                          category === 'pre-roll' ? 'Pre-rolls' : 
                                          category.charAt(0).toUpperCase() + category.slice(1);
                        
                        const handleClick = (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Navigating to category:', category);
                          navigate(`/products?category=${category}`);
                          setIsProductsDropdownOpen(false);
                          setIsMobileMenuOpen(false);
                        };
                        
                        return (
                          <div
                            key={category}
                            onClick={handleClick}
                            onTouchEnd={handleClick}  // ADD THIS
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              padding: '0.75rem',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              border: '1px solid transparent',
                              WebkitTapHighlightColor: 'transparent',  // ADD THIS
                              touchAction: 'manipulation'  // ADD THIS
                            }}
                          >
                            <div style={{ 
                              marginBottom: '0.25rem', 
                              transform: 'scale(0.85)',
                              pointerEvents: 'none'  // ADD THIS
                            }}>
                              <IconComponent />
                            </div>
                            <span style={{
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              color: '#e5e5e5',
                              textAlign: 'center',
                              pointerEvents: 'none'
                            }}>
                              {displayName}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    <h4 style={{
                      margin: '0 0 0.75rem 0',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#e5e5e5',
                      borderBottom: `1px solid ${colors.border}`,
                      paddingBottom: '0.5rem'
                    }}>
                      Shop by Type
                    </h4>

                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      marginBottom: '1rem'
                    }}>
                      {/* Indica */}
                      <div
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/products?strain_type=Indica`);
                            setIsProductsDropdownOpen(false);
                            setIsMobileMenuOpen(false);
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/products?strain_type=Indica`);
                            setIsProductsDropdownOpen(false);
                            setIsMobileMenuOpen(false);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.75rem',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            backgroundColor: 'rgba(147, 51, 234, 0.1)',
                            border: '1px solid rgba(147, 51, 234, 0.3)',
                            WebkitTapHighlightColor: 'transparent',
                            touchAction: 'manipulation'
                          }}
                        >
                        <svg width="40" height="40" viewBox="0 0 60 60" style={{ pointerEvents: 'none' }}>
                          <image 
                            href="https://highmind-bucket.s3.us-east-1.amazonaws.com/indica-removebg-preview.png" 
                            height="40" width="40" x="0" y="0"
                            style={{ pointerEvents: 'none' }}
                          />
                        </svg>
                        <div style={{ flex: 1, pointerEvents: 'none' }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#9333ea' }}>
                            Indica
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#888' }}>
                            Relaxing
                          </div>
                        </div>
                      </div>

                      {/* Hybrid */}
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/products?strain_type=Hybrid`);
                          setIsProductsDropdownOpen(false);
                          setIsMobileMenuOpen(false);
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/products?strain_type=Hybrid`);
                          setIsProductsDropdownOpen(false);
                          setIsMobileMenuOpen(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                          border: '1px solid rgba(16, 185, 129, 0.3)',
                          WebkitTapHighlightColor: 'transparent',
                          touchAction: 'manipulation'
                        }}
                      >
                        <svg width="40" height="40" viewBox="0 0 60 60">
                          <image 
                            href="https://highmind-bucket.s3.us-east-1.amazonaws.com/hybird (1).png" 
                            height="40" width="40" x="0" y="0" 
                          />
                        </svg>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#10b981' }}>
                            Hybrid
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#888' }}>
                            Balanced
                          </div>
                        </div>
                      </div>

                      {/* Sativa */}
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/products?strain_type=Sativa`);
                          setIsProductsDropdownOpen(false);
                          setIsMobileMenuOpen(false);
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/products?strain_type=Sativa`);
                          setIsProductsDropdownOpen(false);
                          setIsMobileMenuOpen(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor: 'rgba(245, 158, 11, 0.1)',
                          border: '1px solid rgba(245, 158, 11, 0.3)',
                          WebkitTapHighlightColor: 'transparent',
                          touchAction: 'manipulation'
                        }}
                      >
                        <svg width="40" height="40" viewBox="0 0 60 60">
                          <image 
                            href="https://highmind-bucket.s3.us-east-1.amazonaws.com/sativa (2).png" 
                            height="40" width="40" x="0" y="0" 
                          />
                        </svg>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#f59e0b' }}>
                            Sativa
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#888' }}>
                            Energizing
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem'
                    }}>
                      <button
                        onClick={() => {
                          navigate('/products?sort=rating');
                          setIsProductsDropdownOpen(false);
                          setIsMobileMenuOpen(false);
                        }}
                        style={{
                          padding: '0.625rem',
                          background: 'rgba(245, 158, 11, 0.2)',
                          color: '#f59e0b',
                          border: '1px solid #f59e0b',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        ⭐ Top Rated
                      </button>
                      
                      <button
                        onClick={() => {
                          navigate('/products?filter=new');
                          setIsProductsDropdownOpen(false);
                          setIsMobileMenuOpen(false);
                        }}
                        style={{
                          padding: '0.625rem',
                          background: 'rgba(0, 212, 170, 0.2)',
                          color: '#00d4aa',
                          border: '1px solid #00d4aa',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        🆕 New Arrivals
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Other nav items */}
              {navigationItems.map(item => {
                const isActive = isActiveRoute(item.path);
                return (
                  <a 
                    key={item.name} 
                    onClick={() => handleNavigation(item.name)}
                    style={{
                      color: isActive ? '#00d4aa' : '#ccc',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      fontWeight: isActive ? '600' : '500',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      transition: 'background-color 0.2s ease',
                      backgroundColor: isActive ? 'rgba(0, 212, 170, 0.1)' : 'transparent',
                      borderLeft: isActive ? '3px solid #00d4aa' : '3px solid transparent'
                    }}
                  >
                    {item.name}
                  </a>
                );
              })}
            </nav>
            
            <div style={{ marginTop: '1rem' }}>
              <UnifiedSearchDropdown onDispensarySelect={handleDispensarySelect} />
            </div>
          </div>
        )}
      </header>

      {/* Dispensary Detail Modal */}
      <DispensaryDetailModal
        isOpen={isDispensaryDetailOpen}
        onClose={() => setIsDispensaryDetailOpen(false)}
        dispensaryId={selectedDispensaryId}
      />
    </>
  );
}